import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import api from '../../../../../api/client';
import HistoryIcon from '@mui/icons-material/History';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TimerOffIcon from '@mui/icons-material/TimerOff';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

const TotalCreditsInfoModal = ({ isOpen, onClose, numericData }) => {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [activeSubTab, setActiveSubTab] = useState('buckets'); // 'buckets' or 'ledger'

    useEffect(() => {
        if (!isOpen) return;

        const fetchLifetimeCredits = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await api.get('credits/lifetime/');
                setData(response.data);
            } catch (err) {
                console.error('Error fetching lifetime credits:', err);
                setError(t('dashboard.failedToLoadCredits'));
            } finally {
                setLoading(false);
            }
        };

        fetchLifetimeCredits();
    }, [isOpen, t]);

    const formatDate = (dateString) => {
        if (!dateString) return t('dashboard.noExpiry');
        const locale = i18n.language === 'hi' ? 'hi-IN' : i18n.language || 'en-US';
        const date = new Date(dateString);
        return date.toLocaleDateString(locale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getBucketStatusBadgeClass = (status) => {
        switch (status) {
            case 'active':
                return 'bg-green-100 text-green-800 border border-green-200';
            case 'expired':
                return 'bg-red-100 text-red-800 border border-red-200';
            case 'used':
                return 'bg-gray-100 text-gray-800 border border-gray-200';
            default:
                return 'bg-blue-100 text-blue-800 border border-blue-200';
        }
    };

    const getLedgerTypeBadgeClass = (type) => {
        switch (type) {
            case 'addition':
                return 'bg-green-100 text-green-800';
            case 'usage':
                return 'bg-blue-100 text-blue-800';
            case 'expiry':
                return 'bg-red-100 text-red-800';
            case 'refund':
                return 'bg-purple-100 text-purple-800';
            case 'adjustment':
                return 'bg-orange-100 text-orange-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (!isOpen) return null;

    // Card values — always use the lifetime API data (not numericData from dashboard summary)
    const totalAdded = data?.lifetime_total_added ?? 0;
    const totalUsed = data?.lifetime_total_used ?? 0;
    const currentBalanceVal = data?.current_balance ?? 0;
    const totalExpiredVal = data?.lifetime_total_expired ?? 0;

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-[10000] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div
                className="bg-white rounded-3xl md:w-[75%] lg:w-[65%] w-[95%] max-h-[92vh] flex flex-col shadow-2xl animate-in fade-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-white rounded-t-3xl">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-violet-50 text-violet-600">
                            <HistoryIcon fontSize="medium" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
                                {t('dashboard.totalCreditsBreakdown')}
                            </h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                {t('dashboard.totalCreditsBreakdownDesc')}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-gray-900 transition-all duration-200"
                    >
                        <svg className="w-5.5 h-5.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto bg-gray-50/50 p-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mb-4"></div>
                            <p className="text-gray-500 font-medium">{t('dashboard.loadingCreditsHistory')}</p>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center my-10 max-w-md mx-auto">
                            <span className="text-3xl">⚠️</span>
                            <h3 className="text-red-800 font-semibold text-lg mt-2">{t('common.error')}</h3>
                            <p className="text-red-600 text-sm mt-1">{error}</p>
                            <button
                                onClick={onClose}
                                className="mt-4 px-5 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors text-sm font-medium"
                            >
                                {t('common.cancel')}
                            </button>
                        </div>
                    ) : data ? (
                        <>
                            {/* Clarifying Disclaimer Banner */}
                            <div className="relative overflow-hidden bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-4 mb-6 flex items-center gap-4 shadow-lg shadow-violet-100">
                                <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, white 0%, transparent 60%)' }} />
                                <div className="p-2.5 rounded-xl bg-white/20 text-white shrink-0">
                                    <HistoryIcon fontSize="small" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-white/20 text-white uppercase tracking-wider">
                                            Lifetime Total
                                        </span>
                                    </div>
                                    <p className="text-white/90 text-xs leading-relaxed">
                                        {t('dashboard.lifetimeCreditsNote')}
                                    </p>
                                </div>
                            </div>


                            {/* Summary Cards Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex flex-col justify-between">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            {t('dashboard.totalAdded')}
                                        </span>
                                        <div className="p-1 rounded-lg bg-green-50 text-green-600">
                                            <TrendingUpIcon fontSize="small" />
                                        </div>
                                    </div>
                                    <div className="text-2xl font-bold text-gray-900">
                                        {totalAdded}
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex flex-col justify-between">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            {t('dashboard.totalUsed')}
                                        </span>
                                        <div className="p-1 rounded-lg bg-blue-50 text-blue-600">
                                            <TrendingDownIcon fontSize="small" />
                                        </div>
                                    </div>
                                    <div className="text-2xl font-bold text-gray-900">
                                        {totalUsed}
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex flex-col justify-between">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            {t('dashboard.totalExpired')}
                                        </span>
                                        <div className="p-1 rounded-lg bg-red-50 text-red-600">
                                            <TimerOffIcon fontSize="small" />
                                        </div>
                                    </div>
                                    <div className="text-2xl font-bold text-gray-900">
                                        {totalExpiredVal}
                                    </div>
                                </div>

                                <div className="bg-white rounded-2xl p-4 border border-violet-100 bg-gradient-to-br from-violet-50/20 to-white shadow-sm flex flex-col justify-between">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-semibold text-violet-700 uppercase tracking-wider">
                                            {t('dashboard.currentBalance')}
                                        </span>
                                        <div className="p-1 rounded-lg bg-violet-50 text-violet-600">
                                            <AccountBalanceWalletIcon fontSize="small" />
                                        </div>
                                    </div>
                                    <div className="text-2xl font-bold text-violet-700">
                                        {currentBalanceVal}
                                    </div>
                                </div>
                            </div>
                            {/* Segmented Controls for Sub-tabs */}
                            <div className="flex border-b border-gray-200 mb-4 bg-white p-1 rounded-xl w-fit shadow-sm border border-gray-100">
                                <button
                                    onClick={() => setActiveSubTab('buckets')}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${activeSubTab === 'buckets'
                                        ? 'bg-violet-600 text-white shadow-md shadow-violet-200'
                                        : 'text-gray-500 hover:text-gray-900'
                                        }`}
                                >
                                    <LocalActivityIcon fontSize="small" />
                                    {t('dashboard.creditBuckets')}
                                </button>
                                <button
                                    onClick={() => setActiveSubTab('ledger')}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${activeSubTab === 'ledger'
                                        ? 'bg-violet-600 text-white shadow-md shadow-violet-200'
                                        : 'text-gray-500 hover:text-gray-900'
                                        }`}
                                >
                                    <HistoryIcon fontSize="small" />
                                    {t('dashboard.transactionLedger')}
                                </button>
                            </div>

                            {/* Tables section */}
                            {activeSubTab === 'buckets' ? (
                                <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                                    {data?.buckets && data.buckets.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse">
                                                <thead>
                                                    <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase">
                                                        <th className="p-4">{t('dashboard.bucketId')}</th>
                                                        <th className="p-4">{t('dashboard.originalAmount')}</th>
                                                        <th className="p-4">{t('dashboard.remainingBalance')}</th>
                                                        <th className="p-4">{t('dashboard.addedOn')}</th>
                                                        <th className="p-4">{t('dashboard.expiry')}</th>
                                                        <th className="p-4">{t('dashboard.notes')}</th>
                                                        <th className="p-4">{t('dashboard.status')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-50 text-sm">
                                                    {data.buckets.map((bucket) => (
                                                        <tr key={bucket.id} className="hover:bg-violet-50/10 transition-colors">
                                                            <td className="p-4 font-semibold text-gray-700">#{bucket.id}</td>
                                                            <td className="p-4 font-bold text-gray-900">{bucket.original_amount}</td>
                                                            <td className="p-4 font-bold text-violet-600">{bucket.remaining_balance}</td>
                                                            <td className="p-4 text-gray-600">{formatDate(bucket.created_at)}</td>
                                                            <td className="p-4 text-gray-600">{formatDate(bucket.expiry_date)}</td>
                                                            <td className="p-4 text-gray-500 max-w-[200px] truncate" title={bucket.notes}>
                                                                {bucket.notes || '-'}
                                                            </td>
                                                            <td className="p-4">
                                                                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase ${getBucketStatusBadgeClass(bucket.status)}`}>
                                                                    {bucket.status}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="p-12 text-center text-gray-400 font-medium">
                                            {t('dashboard.noUpcomingExpiries')}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                                    {data?.ledger && data.ledger.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left border-collapse">
                                                <thead>
                                                    <tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase">
                                                        <th className="p-4">{t('dashboard.ledgerDate')}</th>
                                                        <th className="p-4">{t('dashboard.ledgerEvent')}</th>
                                                        <th className="p-4">{t('dashboard.ledgerAmount')}</th>
                                                        <th className="p-4">{t('dashboard.notes')}</th>
                                                        <th className="p-4">{t('dashboard.bucketId')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-50 text-sm">
                                                    {data.ledger.map((entry) => (
                                                        <tr key={entry.id} className="hover:bg-violet-50/10 transition-colors">
                                                            <td className="p-4 text-gray-600">{formatDate(entry.created_at)}</td>
                                                            <td className="p-4">
                                                                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase ${getLedgerTypeBadgeClass(entry.entry_type)}`}>
                                                                    {entry.entry_type}
                                                                </span>
                                                            </td>
                                                            <td className={`p-4 font-bold text-base ${entry.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                                {entry.amount > 0 ? `+${entry.amount}` : entry.amount}
                                                            </td>
                                                            <td className="p-4 text-gray-500 max-w-[280px] truncate" title={entry.notes}>
                                                                {entry.notes || '-'}
                                                            </td>
                                                            <td className="p-4 text-gray-600">{entry.bucket_id ? `#${entry.bucket_id}` : '-'}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="p-12 text-center text-gray-400 font-medium">
                                            No transaction logs found.
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    ) : null}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-100 flex justify-end bg-white rounded-b-3xl">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all duration-200 shadow-sm"
                    >
                        {t('common.cancel')}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default TotalCreditsInfoModal;
